import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Snackbar,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Button,
  Chip
} from '@mui/material';
import { RestoreFromTrash, DeleteForever, Refresh, History, DeleteOutline, Visibility } from '@mui/icons-material';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../apiBase';

const ENTITY_TYPES = [
  { value: 'articles', label: 'Articles' },
  { value: 'vendors', label: 'Vendors' },
  { value: 'daily_vendor_followup', label: 'Vendor Follow Up' },
  { value: 'daily_head_office', label: 'Head Office Transactions' },
  { value: 'hardcopy_sheet', label: 'Hardcopy Sheets' },
  { value: 'multi_hardcopy_sheet', label: 'Multi Hardcopy Sheets' },
];

export default function RestoreBin() {
  const token = localStorage.getItem('token');
  const [selectedType, setSelectedType] = useState('articles');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState('');
  
  // Confirmation Dialog
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState(null); // 'restore' or 'delete'
  const [selectedItem, setSelectedItem] = useState(null);

  // View Details Dialog
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!selectedType) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/restore/${selectedType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch deleted items');
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedType, token]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleActionClick = (type, item) => {
    setActionType(type);
    setSelectedItem(item);
    setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    if (!selectedItem || !actionType) return;
    
    try {
      const method = actionType === 'restore' ? 'POST' : 'DELETE';
      // For DELETE, pass sourceType as query param
      // For POST, pass sourceType in body
      let url = `${API_BASE_URL}/api/restore/${selectedType}/${selectedItem.id}`;
      let body = null;

      if (actionType === 'restore') {
         body = JSON.stringify({ sourceType: selectedItem.source_type });
      } else {
         url += `?sourceType=${selectedItem.source_type}`;
      }

      const res = await fetch(url, {
        method,
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body
      });
      
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Action failed');
      }

      setSuccessMsg(actionType === 'restore' ? 'Item restored successfully' : 'Item permanently deleted');
      fetchItems();
    } catch (err) {
      setError(err.message);
    } finally {
      setConfirmOpen(false);
      setSelectedItem(null);
      setActionType(null);
    }
  };

  const handleView = (item) => {
    setViewItem(item);
    setViewOpen(true);
  };

  // Helper to render data details
  const renderDataDetails = (data, currentData = null, isVersion = false) => {
    if (!data) return 'N/A';
    
    if (isVersion && currentData) {
       // Find all keys from the formatted data to display
       const keys = Object.keys(data);
       
       return (
         <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, minWidth: 500 }}>
           <Box sx={{ p: 1.5, bgcolor: '#fff5f5', borderRadius: 1, border: '1px solid #ffc9c9' }}>
             <Typography variant="subtitle2" color="error" sx={{ mb: 1, fontWeight: 'bold' }}>Previous Value (Restorable)</Typography>
             {keys.map((key) => {
               const val1 = data[key];
               const val2 = currentData[key];
               const isDiff = String(val1) !== String(val2);

               return (
                 <Box key={key} sx={{ 
                   fontSize: '0.85rem', 
                   mb: 0.5, 
                   wordBreak: 'break-word',
                   bgcolor: isDiff ? '#ffe0e0' : 'transparent',
                   fontWeight: isDiff ? 'bold' : 'normal',
                   p: isDiff ? 0.5 : 0,
                   borderRadius: 1
                 }}>
                   <strong>{key}:</strong> {String(val1)}
                 </Box>
               );
             })}
           </Box>
           <Box sx={{ p: 1.5, bgcolor: '#f0fff4', borderRadius: 1, border: '1px solid #c6f6d5' }}>
             <Typography variant="subtitle2" color="success.main" sx={{ mb: 1, fontWeight: 'bold' }}>Current Value</Typography>
             {keys.map((key) => {
               const val1 = data[key];
               const val2 = currentData[key];
               const isDiff = String(val1) !== String(val2);

               return (
                 <Box key={key} sx={{ 
                   fontSize: '0.85rem', 
                   mb: 0.5, 
                   wordBreak: 'break-word',
                   bgcolor: isDiff ? '#e6fffa' : 'transparent',
                   fontWeight: isDiff ? 'bold' : 'normal',
                   p: isDiff ? 0.5 : 0,
                   borderRadius: 1
                 }}>
                   <strong>{key}:</strong> {String(val2 !== undefined ? val2 : 'N/A')}
                 </Box>
               );
             })}
           </Box>
         </Box>
       );
    }

    return Object.entries(data).map(([key, value]) => (
      <Box key={key} component="span" sx={{ mr: 2, display: 'inline-block' }}>
        <strong>{key}:</strong> {String(value)}
      </Box>
    ));
  };

  return (
    <Layout title="Restore Point">
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <FormControl sx={{ minWidth: 250 }}>
            <InputLabel>Entity Type</InputLabel>
            <Select
              value={selectedType}
              label="Entity Type"
              onChange={(e) => setSelectedType(e.target.value)}
            >
              {ENTITY_TYPES.map((type) => (
                <MenuItem key={type.value} value={type.value}>
                  {type.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <Button 
            variant="outlined" 
            startIcon={<Refresh />} 
            onClick={fetchItems}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Action Info</TableCell>
                <TableCell>Details</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    {loading ? 'Loading...' : 'No items found'}
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      {item.source_type === 'version' ? (
                        <Chip icon={<History />} label="Previous Version" color="info" size="small" variant="outlined" />
                      ) : (
                        <Chip icon={<DeleteOutline />} label="Deleted Item" color="error" size="small" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="caption" display="block">
                          {item.source_type === 'version' ? 'Modified By:' : 'Deleted By:'} {item.deleted_by_name || item.deleted_by || 'Unknown'}
                        </Typography>
                        <Typography variant="caption" display="block" color="textSecondary">
                          At: {new Date(item.deleted_at).toLocaleString()}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ maxWidth: 800, overflow: 'hidden' }}>
                        {renderDataDetails(item.data, item.current_data, item.source_type === 'version')}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton color="info" onClick={() => handleView(item)}>
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Restore">
                        <IconButton color="primary" onClick={() => handleActionClick('restore', item)}>
                          <RestoreFromTrash />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Permanently Delete">
                        <IconButton color="error" onClick={() => handleActionClick('delete', item)}>
                          <DeleteForever />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* View Details Dialog */}
        <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="lg" fullWidth>
          <DialogTitle>
             {viewItem?.source_type === 'version' ? 'Version Comparison Details' : 'Item Details'}
          </DialogTitle>
          <DialogContent dividers>
            {viewItem && viewItem.details ? (
               viewItem.source_type === 'version' && viewItem.current_details ? (
                  // Comparison View
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                     {/* Left: Previous */}
                     <Box>
                        <Typography variant="h6" color="error" gutterBottom>Previous Value</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                           {Object.keys(viewItem.details).map(key => {
                              const val1 = viewItem.details[key];
                              const val2 = viewItem.current_details[key];
                              const isDiff = String(val1) !== String(val2);
                              
                              return (
                                <Box key={key} sx={{ 
                                   p: 1, border: '1px solid #eee', borderRadius: 1,
                                   bgcolor: isDiff ? '#ffe0e0' : 'transparent'
                                }}>
                                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                    {key.replace(/_/g, ' ').toUpperCase()}
                                  </Typography>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: isDiff ? 'bold' : 'normal' }}>
                                    {val1 === null ? 'null' : String(val1)}
                                  </Typography>
                                </Box>
                              );
                           })}
                        </Box>
                     </Box>
                     
                     {/* Right: Current */}
                     <Box>
                        <Typography variant="h6" color="success.main" gutterBottom>Current Value</Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 2 }}>
                           {Object.keys(viewItem.details).map(key => {
                              const val1 = viewItem.details[key];
                              const val2 = viewItem.current_details[key];
                              const isDiff = String(val1) !== String(val2);
                              
                              return (
                                <Box key={key} sx={{ 
                                   p: 1, border: '1px solid #eee', borderRadius: 1,
                                   bgcolor: isDiff ? '#e6fffa' : 'transparent'
                                }}>
                                  <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                    {key.replace(/_/g, ' ').toUpperCase()}
                                  </Typography>
                                  <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: isDiff ? 'bold' : 'normal' }}>
                                    {val2 === undefined ? 'N/A' : (val2 === null ? 'null' : String(val2))}
                                  </Typography>
                                </Box>
                              );
                           })}
                        </Box>
                     </Box>
                  </Box>
               ) : (
                  // Single View (Deleted Item)
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 2 }}>
                    {Object.entries(viewItem.details).map(([key, value]) => (
                      <Box key={key} sx={{ p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                        <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                          {key.replace(/_/g, ' ').toUpperCase()}
                        </Typography>
                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                          {value === null ? 'null' : String(value)}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
               )
            ) : (
              <Typography>No details available</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>
            {actionType === 'restore' ? 'Restore Item' : 'Permanent Delete'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              {actionType === 'restore' 
                ? 'Are you sure you want to restore this item? It will appear in the main list again.' 
                : 'Are you sure you want to permanently delete this item? This action CANNOT be undone.'}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirm} color={actionType === 'delete' ? 'error' : 'primary'} autoFocus>
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar 
          open={!!successMsg} 
          autoHideDuration={6000} 
          onClose={() => setSuccessMsg('')}
        >
          <Alert onClose={() => setSuccessMsg('')} severity="success">
            {successMsg}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}
